import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useFeatureFlag, FLAGS } from "../config/featureFlags";

/**
 * InstallNudge — mobile Stage 1 (docs in optimus-frameworks/docs/
 * mobile-os-plan.md). Shows after the user's first solid back-and-forth
 * (Brad 2026-09-01: "like 5 messages is fine"), never in an installed app,
 * dismiss remembered forever.
 *
 * Android/desktop Chrome: uses the captured beforeinstallprompt
 * (window.pwaInstallPrompt — wired in public/index.html since the hf pass,
 * never surfaced in UI until now). iOS Safari: no prompt API exists, so we
 * show the Share → Add to Home Screen path.
 */
const DISMISS_KEY = "optimus_install_nudge_dismissed";
const MIN_MESSAGES = 5;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const InstallNudge = ({ messageCount = 0 }) => {
  const enabled = useFeatureFlag(FLAGS.PWA_INSTALL_NUDGE);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (
    !enabled ||
    dismissed ||
    installed ||
    isStandalone() ||
    messageCount < MIN_MESSAGES ||
    (!window.pwaInstallPrompt && !isIOS())
  ) {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const install = async () => {
    const p = window.pwaInstallPrompt;
    if (!p) return;
    p.prompt();
    try {
      const { outcome } = await p.userChoice;
      if (outcome === "accepted") setInstalled(true);
    } catch (_) { /* user closed the sheet — nudge stays dismissible */ }
    window.pwaInstallPrompt = null;
  };

  return (
    <div
      role="complementary"
      aria-label="Install Optimus"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "8px 12px 0",
        padding: "10px 14px",
        borderRadius: 12,
        background: "var(--bg-accent-subtle, rgba(102,126,234,.12))",
        border: "1px solid var(--border-primary)",
        fontSize: 13.5,
      }}
    >
      <span style={{ fontSize: 18 }}>📲</span>
      <span style={{ flex: 1, color: "var(--text-primary)" }}>
        {window.pwaInstallPrompt ? (
          <>
            <strong>Take Optimus with you</strong> — install it like an app,
            straight from here.
          </>
        ) : (
          <>
            <strong>Take Optimus with you</strong> — tap Share, then{" "}
            <em>Add to Home Screen</em>.
          </>
        )}
      </span>
      {window.pwaInstallPrompt && (
        <button
          onClick={install}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: "none",
            background: "var(--accent-primary)",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="Dismiss install suggestion"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--text-secondary)",
          padding: 2,
        }}
      >
        <X size={15} />
      </button>
    </div>
  );
};

export default InstallNudge;
